import type { SnowplowClient } from '@nextmar/snowplow-sdk';
import { ensureClassicTracker } from '../../analyticsLoader';
import { getSensorsReady } from '../../providers/sensorProvider';
import {
  captureTrackingEvent,
  getEarlyEventQueue,
  type CapturedEventInput,
  type CaptureResult,
} from '../../earlyEvents';
import logger from '@/utils/client-logger';
import type { Provider, TrackingEvent } from '../../types';
import { ProviderType } from '../../types';

/** ReportingCenter 使用的 SDK 薄 Provider，不实现队列、Schema、ID 或 tracker。 */
export class SnowplowSdkProvider implements Provider {
  /** ReportingCenter 的稳定 Provider 标识。 */
  readonly type = ProviderType.SNOWPLOW;

  /** ReportingCenter 过滤 Provider 时使用的最终远程解析开关。 */
  readonly isEnabled: boolean;

  /** 只有 SDK init 返回 ready 时才为 true。 */
  private initialized = false;

  /** App 旧bridge只接受ready之后同页同阶段的实时事件。 */
  private readyAt = Infinity;

  /** 保存同一个 singleton Client 与明确开关。 */
  constructor(
    private readonly client: SnowplowClient,
    isEnabled: boolean,
  ) {
    this.isEnabled = isEnabled;
  }

  /** 初始化唯一 SDK Client，并把 ready 状态转换为 ReportingCenter boolean。 */
  async init(): Promise<boolean> {
    try {
      if (!this.isEnabled) return false;
      if (typeof window !== 'undefined') {
        await getSensorsReady();
        if (this.client.getState().runtime === 'browser') await ensureClassicTracker();
      }
      const result = await this.client.init();
      this.initialized = result.status === 'ready';
      if (this.initialized) this.readyAt = Date.now();
      return this.initialized;
    } catch {
      this.initialized = false;
      logger.error('[SnowplowSdkProvider] init_failed');
      return false;
    }
  }

  /** 所有旧调用也先捕获，不在ready后重新解释当前页面。 */
  track(event: TrackingEvent): void {
    captureTrackingEvent(event, [ProviderType.SNOWPLOW]);
  }

  /** Browser使用窄快照API；未知派发结果不会被外部队列盲目重试。 */
  async trackCaptured(input: CapturedEventInput): Promise<CaptureResult> {
    if (!this.initialized) return { accepted: false, reason: 'not_ready' };
    try {
      if (this.client.getState().runtime === 'webview') {
        // 现有原生桥不支持历史时间；只保留同页同用户的实时旧协议路径。
        if (
          input.identityEpoch !== getEarlyEventQueue()?.getIdentityEpoch() ||
          input.occurredAt < this.readyAt ||
          typeof location === 'undefined' ||
          input.page.url !== location.href ||
          Date.now() - input.occurredAt > 1000
        )
          return { accepted: false, reason: 'unsupported_runtime' };
        switch (input.event.kind) {
          case 'track':
            return this.client.track(
              input.event.eventName,
              input.event.properties,
              input.event.options,
            );
          case 'pageView':
            return this.client.trackPageView(input.event.properties);
          case 'webClick':
            return this.client.trackWebClick(input.event.properties);
          case 'webExp':
            return this.client.trackWebExp(input.event.properties);
          case 'signUp':
            return this.client.trackSignUp(input.event.user);
          default:
            return { accepted: false, reason: 'unsupported_runtime' };
        }
      }
      return await this.client.trackCapturedEvent(input);
    } catch {
      logger.error('[SnowplowSdkProvider] captured_dispatch_unknown');
      return { accepted: false, reason: 'dispatch_unknown' };
    }
  }

  /** 返回 ReportingCenter 可消费的初始化状态。 */
  isInitialized(): boolean {
    return this.initialized;
  }
}
